import { useState } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { api } from '../api/client.js';
import {
    OBSERVADOR_BODY_SIGNALS,
    OBSERVADOR_DOMAIN_OPTIONS,
    OBSERVADOR_REFLECTION_CHIPS,
    OBSERVADOR_SUPPORT_NEEDS,
    OBSERVADOR_WORK_FRICTION,
    checkinPhaseLabel,
} from '../data/observadorFormOptions.js';

function frictionToneLabel(level) {
    if (level === 'red') return 'Hoy conviene acercarte a alguien de confianza.';
    if (level === 'yellow') return 'Hoy merece la pena cuidarte un poco más.';
    return 'Por ahora vas en buen equilibrio.';
}

/**
 * Observador integrado en columna 3 del supervisor (parte de la manada).
 * Los registros van a friction_diagnostics y el admin los ve en coach / mapa.
 */
export default function SupervisorObservadorPanel({ onCompleted, compact = false, viewingWorkerName = null }) {
    const [energyLevel, setEnergyLevel] = useState(4);
    const [bodySignal, setBodySignal] = useState('estable');
    const [workFriction, setWorkFriction] = useState('baja');
    const [supportNeed, setSupportNeed] = useState('claridad');
    const [selectedDomain, setSelectedDomain] = useState('');
    const [reflectionText, setReflectionText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [insight, setInsight] = useState(null);
    const { user } = useAuth();

    function appendChip(text) {
        setReflectionText((prev) => (prev ? `${prev.trim()} ${text}`.trim() : text));
    }

    function chipIsActive(text) {
        return reflectionText.includes(text);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setSubmitting(true);
        setError('');
        try {
            const res = await api.analyzeObservadorForm({
                energyLevel: Number(energyLevel),
                bodySignal,
                workFriction,
                supportNeed,
                selectedDomain: selectedDomain || '',
                reflectionText: reflectionText.trim(),
            });
            if (!res?.success || !res.data) {
                setError(res?.error || 'No se pudo guardar tu lectura.');
                return;
            }
            const d = res.data;
            const mainText = [d.leaderGuide?.question, d.leaderGuide?.action].filter(Boolean).join(' ');
            const phase = d.dailyCheckin?.checkinPhase;
            const phaseNote = phase ? checkinPhaseLabel(phase) : 'Tu check-in quedó guardado';
            const completeNote = d.dailyCheckin?.dailyCheckinComplete
                ? ' Llegada y cierre de hoy completos.'
                : phase === 'start'
                  ? ' Por la tarde puedes volver para registrar el cierre.'
                  : '';
            setInsight({
                tone: frictionToneLabel(d.frictionLevel),
                mainText: mainText || `${phaseNote}.${completeNote}`,
                epaLine:
                    d.epaCapsule?.title && d.epaCapsule?.steps?.[0]
                        ? `${d.epaCapsule.title}: ${d.epaCapsule.steps[0]}`
                        : '',
                frictionLevel: d.frictionLevel,
                checkinPhase: phase,
            });
            setReflectionText('');
            onCompleted?.();
        } catch (err) {
            setError(err.message || 'No se pudo guardar tu lectura.');
        } finally {
            setSubmitting(false);
        }
    }

    function resetForm() {
        setInsight(null);
        setError('');
    }

    return (
        <section className={`sup-obs${compact ? ' sup-obs-compact' : ''}`} aria-label="Tu observador">
            <p className="sup-section-label">Observador</p>
            {!compact ? (
                <p className="sup-team-hint sup-obs-lead">
                    <strong>Tu check-in del día</strong> (solo sobre ti, no sobre tu equipo). La primera vez cuenta como
                    llegada; si vuelves a guardar hoy, registra el cierre. El administrador ve agregados, no tu frase literal.
                </p>
            ) : (
                <p className="sup-team-hint sup-obs-lead">
                    Tu check-in personal{viewingWorkerName ? ` (no es el de ${viewingWorkerName})` : ''}.
                </p>
            )}

            {insight ? (
                <div className={`sup-obs-result level-${insight.frictionLevel || 'green'}`}>
                    <p className="sup-obs-result-tone">{insight.tone}</p>
                    <p className="sup-obs-result-text">{insight.mainText}</p>
                    {insight.epaLine ? <p className="sup-obs-result-epa">{insight.epaLine}</p> : null}
                    {onCompleted ? (
                        <button type="button" className="sup-obs-submit" onClick={onCompleted}>
                            Ir a mi panel de equipo
                        </button>
                    ) : null}
                    <button type="button" className="sup-obs-reset" onClick={resetForm}>
                        Registrar otra lectura
                    </button>
                </div>
            ) : (
                <form className="sup-obs-form" onSubmit={handleSubmit}>
                    <label className="sup-obs-field">
                        <span className="sup-obs-field-label">Energía ({energyLevel}/6)</span>
                        <input
                            type="range"
                            min="1"
                            max="6"
                            value={energyLevel}
                            onChange={(ev) => setEnergyLevel(Number(ev.target.value))}
                        />
                    </label>

                    <p className="sup-obs-field-label">Tu cuerpo ahora</p>
                    <div className="sup-obs-chips">
                        {OBSERVADOR_BODY_SIGNALS.map((o) => (
                            <button
                                key={o.value}
                                type="button"
                                className={`sup-obs-chip${bodySignal === o.value ? ' on' : ''}`}
                                onClick={() => setBodySignal(o.value)}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>

                    <div className="sup-obs-row">
                        <label className="sup-obs-field">
                            <span className="sup-obs-field-label">Carga trabajo</span>
                            <select value={workFriction} onChange={(ev) => setWorkFriction(ev.target.value)}>
                                {OBSERVADOR_WORK_FRICTION.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="sup-obs-field">
                            <span className="sup-obs-field-label">Te ayudaría</span>
                            <select value={supportNeed} onChange={(ev) => setSupportNeed(ev.target.value)}>
                                {OBSERVADOR_SUPPORT_NEEDS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <p className="sup-obs-field-label">¿Dónde sientes más peso? (vital ALINEA)</p>
                    <div className="sup-obs-domains">
                        {OBSERVADOR_DOMAIN_OPTIONS.map((d) => (
                            <button
                                key={d.value}
                                type="button"
                                className={`sup-obs-domain${selectedDomain === d.value ? ' on' : ''}`}
                                onClick={() => setSelectedDomain(d.value)}
                                title={d.hint}
                            >
                                <strong>{d.label}</strong>
                                <span>{d.alineaBrief}</span>
                            </button>
                        ))}
                    </div>

                    <p className="sup-obs-field-label">Atajos (se añaden a tu frase)</p>
                    <div className="sup-obs-chips sup-obs-chips-wrap">
                        {OBSERVADOR_REFLECTION_CHIPS.map((c) => (
                            <button
                                key={c.text}
                                type="button"
                                className={`sup-obs-chip${chipIsActive(c.text) ? ' on' : ''}`}
                                onClick={() => appendChip(c.text)}
                                aria-pressed={chipIsActive(c.text)}
                            >
                                {c.emoji} {c.text}
                            </button>
                        ))}
                    </div>

                    <label className="sup-obs-field">
                        <span className="sup-obs-field-label">Tu frase (opcional)</span>
                        <textarea
                            rows={3}
                            value={reflectionText}
                            onChange={(ev) => setReflectionText(ev.target.value)}
                            placeholder="Unas líneas bastan…"
                        />
                    </label>

                    {error ? (
                        <p className="sup-obs-error" role="alert">
                            {error}
                        </p>
                    ) : null}

                    <button type="submit" className="sup-obs-submit" disabled={submitting}>
                        {submitting ? 'Guardando…' : 'Ver lectura y guardar'}
                    </button>
                </form>
            )}
        </section>
    );
}
